import { RefObject, useCallback, useEffect, useRef } from "react";

import { usePathname } from "next/navigation";

import { GA4 } from "@/utils/ga4Utils";
import { isProductPath } from "@/utils/general";
import { getPageScrollTop } from "@/utils/getPageScrollTop";
import { segmentEvent } from "@/utils/segmentfunctions";

import { useIsMobile } from "./useIsMobile";

const PAGE_SCROLLED_ENTER_PX = 85;
const PAGE_SCROLLED_EXIT_PX = 20;

const isZeLoaded = () =>
  typeof window !== "undefined" && !!(window.zE && typeof window.zE.show === "function");
const isAdaLoaded = () => typeof window !== "undefined" && !!window.adaEmbed;
const isAttentiveLoaded = () => typeof window !== "undefined" && !!window.attentive_overlay;

interface UseHeaderEffectsParams {
  isSearchVisible: boolean;
  closeSearchDropdown: (runSegmentEvent?: boolean) => void;
  setIsPdp: (value: boolean) => void;
  navIsOpen: boolean;
  setNavIsOpen: (value: boolean) => void;
  setMenuIsOpen: (value: boolean) => void;
}

interface UseHeaderEffectsReturn {
  isMouseInHeader: RefObject<boolean>;
  onLeave: () => void;
  onEnter: () => void;
  handleNavLogoExitClick: () => void;
  handleScrollClass: () => void;
  toggleMobileNav: (isOpen?: boolean) => void;
}

/**
 * Custom hook to encapsulate header-related state, effects, and interactive methods.
 * It manages mobile navigation, search visibility, scroll behaviors, and third-party script interactions.
 */
export const useHeaderEffects = ({
  isSearchVisible,
  closeSearchDropdown,
  setIsPdp,
  navIsOpen,
  setNavIsOpen,
  setMenuIsOpen,
}: UseHeaderEffectsParams): UseHeaderEffectsReturn => {
  const isMouseInHeader = useRef<boolean>(false); // Ref to track mouse position within the header
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const prevPathname = useRef(pathname);

  const handleScrollClass = useCallback(() => {
    const classList = document.body.classList;
    const scrollTopValue = getPageScrollTop();
    const hasPageScrolledClass = classList.contains("pagescrolled");

    if (!isMouseInHeader.current) {
      if (scrollTopValue > PAGE_SCROLLED_ENTER_PX) {
        if (!hasPageScrolledClass) {
          classList.add("pagescrolled");
        }
      } else if (scrollTopValue < PAGE_SCROLLED_EXIT_PX) {
        if (hasPageScrolledClass) {
          classList.remove("pagescrolled");
        }
      }
    }
  }, [isMouseInHeader]);

  const toggleMobileNav = useCallback(
    (isOpen = false) => {
      segmentEvent("Hamburger Navigation Menu Clicked", {
        path: "/",
      });

      if (isAttentiveLoaded()) {
        try {
          window.attentive_overlay!.style.zIndex = isOpen ? "10" : "2147483647";
        } catch {
          //console.error("attentive_overlay zIndex failed ", error);
        }
      }

      if (!isOpen) {
        setNavIsOpen(false);
        setMenuIsOpen(false);
        if (isZeLoaded()) {
          window.zE!.show();
        } else if (isAdaLoaded()) {
          const adaBtn = document.getElementById("ada-button-frame");
          if (adaBtn) {
            adaBtn.style.display = "block";
          }
        }
      } else {
        setNavIsOpen(true);
        if (isAdaLoaded()) {
          const adaBtn = document.getElementById("ada-button-frame");
          if (adaBtn) {
            adaBtn.style.display = "none";
          }
        } else if (isZeLoaded()) {
          window.zE!.hide();
        } else {
          const zeInterval = setInterval(() => {
            if (isZeLoaded()) {
              window.zE!.hide();
              clearInterval(zeInterval);
            }
          }, 250);
        }
      }
    },
    [setNavIsOpen, setMenuIsOpen]
  );

  const handleNavLogoExitClick = useCallback(() => {
    toggleMobileNav(false);
    GA4.onNavigationClicked({
      destination: "/",
      type: "link",
    });
  }, [toggleMobileNav]);

  const onLeave = useCallback(() => {
    isMouseInHeader.current = false;
    handleScrollClass();
  }, [isMouseInHeader, handleScrollClass]);

  const onEnter = useCallback(() => {
    isMouseInHeader.current = true;
  }, [isMouseInHeader]);

  useEffect(() => {
    const handleResize = () => {
      const navEl = document.querySelector(".nav-open");
      if (navEl && window.innerWidth >= 1024) {
        toggleMobileNav(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [toggleMobileNav]);

  useEffect(() => {
    /* Close search when the route actually changes (skip mount / same-path soft nav) */
    if (prevPathname.current !== pathname) {
      prevPathname.current = pathname;
      closeSearchDropdown(false);
    }
  }, [pathname, closeSearchDropdown]);

  useEffect(() => {
    const bodyEl = typeof window !== "undefined" ? document.querySelector("body") : null;
    let adaChat = typeof window !== "undefined" ? document.querySelector("#ada-entry") : null;
    const banners =
      typeof window !== "undefined" ? document.getElementsByClassName("video-banner-wrap") : null;

    const banner = banners && banners.length > 0 ? (banners[0] as HTMLElement) : null;
    const video = banner ? banner.getElementsByTagName("video")[0] : null;

    if (!adaChat) {
      const intervalId = setInterval(() => {
        if (typeof window !== "undefined") {
          adaChat = document.querySelector("#ada-entry");
          if (adaChat && isSearchVisible) {
            adaChat.classList.add("hidden");
            clearInterval(intervalId);
          }
        }
      }, 0);
      return () => clearInterval(intervalId);
    }

    if (isSearchVisible) {
      adaChat?.classList.add("hidden");
      video?.pause();
      if (bodyEl) bodyEl.style.overflow = "hidden";
      return;
    }

    if (bodyEl) bodyEl.style.removeProperty("overflow");
    adaChat?.classList.remove("hidden");
    if (!navIsOpen) {
      // Potentially re-enable scroll
    }
    video?.play();
  }, [isSearchVisible, navIsOpen]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsPdp(isProductPath(window.location.pathname));
    }
  }, [setIsPdp]);

  useEffect(() => {
    const rect = { y: 0 };
    const gap = isMobile ? 0 : 40;

    const handleScroll = () => {
      handleScrollClass();
      if (isProductPath(window.location.pathname)) {
        const classList = document.body.classList;
        const pageScrolledAtc = classList.contains("stickyatc");

        document.querySelectorAll<HTMLElement>(".atc-button").forEach((ela) => {
          if (ela && ela.offsetParent && ela.offsetTop) {
            rect.y = ela.getBoundingClientRect().y;
          }
        });

        if (rect.y && rect.y - gap < 0) {
          if (!pageScrolledAtc) classList.add("stickyatc");
        } else {
          if (pageScrolledAtc) classList.remove("stickyatc");
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScrollClass, isMobile]);

  // Remove sticky class on every route change
  useEffect(() => {
    document.body.classList.remove("stickyatc");
  }, [pathname]);

  return {
    isMouseInHeader,
    onLeave,
    onEnter,
    handleNavLogoExitClick,
    handleScrollClass,
    toggleMobileNav,
  };
};
